import { MasterTranslation } from '@/constants';
import { useGlobalState } from '@/hooks';
import { AnchorWrapper } from '@/modules/pmt/components/AnchorWrapper';
import { SectionContainer } from '@/modules/pmt/components/SectionContainer';
import type { IMerchantInfo } from '@/modules/pmt/interfaces';
import EnvelopeIcon from '@/public/images/envelope.svg';
import GlobeIcon from '@/public/images/globe.svg';
import { type FC, useMemo } from 'react';
import { TRACKING_DETAILS_SECTIONS } from '../../constants';
import {
  MerchantInfo,
  MerchantInfoIconWrapper,
  MerchantInfoItem,
  MerchantInfoItemName,
  MerchantLeftSide,
  MerchantLogo,
  MerchantName,
  MerchantSocialMedia,
  MerchantWrapper,
  SocialMediaImg,
  SocialMediaName,
} from './MerchantSection.styles';
import {
  FacebookProfilePreview,
  InstagramProfilePreview,
  TiktokProfilePreview,
} from './components/SocialProfilePreview';

interface IMerchantSectionProps {
  merchantInfo: IMerchantInfo;
}

const availableFields: Partial<keyof IMerchantInfo>[] = [
  'merchantName',
  'contact',
  'website',
  'instagram',
  'facebook',
  'tiktok',
  'youtube',
];

export const MerchantSection: FC<IMerchantSectionProps> = ({ merchantInfo }) => {
  const { t } = useGlobalState();
  const merchantInfoItemList = useMemo(
    () => [
      {
        key: 'instagram',
        icon: '/images/merchant_insta.png',
        name: `${merchantInfo.merchantName} Instagram`,
        url: merchantInfo.instagram,
      },
      {
        key: 'tiktok',
        icon: '/images/merchant_tiktok.png',
        name: `${merchantInfo.merchantName} Tiktok`,
        url: merchantInfo.tiktok,
      },
      {
        key: 'facebook',
        icon: '/images/merchant_facebook.png',
        name: `${merchantInfo.merchantName} Facebook`,
        url: merchantInfo.facebook,
      },
      {
        key: 'youtube',
        icon: '/images/merchant_youtube.png',
        name: `${merchantInfo.merchantName} Youtube`,
        url: merchantInfo.youtube,
      },
    ],
    [merchantInfo],
  );

  const profilePreview = useMemo(() => {
    if (merchantInfo.instagram) {
      return <InstagramProfilePreview src={merchantInfo.instagram} key={merchantInfo.instagram} />;
    }
    if (merchantInfo.tiktok) {
      return <TiktokProfilePreview src={merchantInfo.tiktok} key={merchantInfo.tiktok} />;
    }
    if (merchantInfo.facebook) {
      return <FacebookProfilePreview src={merchantInfo.facebook} key={merchantInfo.facebook} />;
    }
    return null;
  }, [merchantInfo]);

  if (availableFields.every((field) => !merchantInfo[field])) {
    return null;
  }

  return (
    <SectionContainer>
      <SectionContainer.Header
        gutterBottom
        sectionTitle={TRACKING_DETAILS_SECTIONS.Merchant.titleKey}
        sectionIcon={TRACKING_DETAILS_SECTIONS.Merchant.icon}
      />
      <MerchantWrapper>
        <MerchantLeftSide>
          {merchantInfo.logo && <MerchantLogo src={merchantInfo.logo} alt={merchantInfo.merchantName} />}
          <MerchantInfo>
            <MerchantName>{merchantInfo.merchantName}</MerchantName>
            <MerchantInfo>
              {/* Contact */}
              {merchantInfo.contact && (
                <MerchantInfoItem>
                  <MerchantInfoIconWrapper>
                    <EnvelopeIcon />
                  </MerchantInfoIconWrapper>
                  <MerchantInfoItemName>{merchantInfo.contact}</MerchantInfoItemName>
                </MerchantInfoItem>
              )}

              {/* Website */}
              {merchantInfo.website && (
                <MerchantInfoItem>
                  <AnchorWrapper href={merchantInfo.website} target='_blank' rel='noopener noreferrer'>
                    <MerchantInfoIconWrapper>
                      <GlobeIcon />
                    </MerchantInfoIconWrapper>
                    <MerchantInfoItemName>{`${merchantInfo.merchantName} ${t(
                      MasterTranslation.Website,
                    )}`}</MerchantInfoItemName>
                  </AnchorWrapper>
                </MerchantInfoItem>
              )}

              {/* Social media */}
              <MerchantSocialMedia>
                {merchantInfoItemList.map((item) => {
                  if (!item.url) return null;
                  return (
                    <MerchantInfoItem key={item.key}>
                      <AnchorWrapper href={item.url} target='_blank' rel='noopener noreferrer'>
                        <MerchantInfoIconWrapper>
                          <SocialMediaImg src={item.icon} alt={item.key} size={profilePreview ? 'large' : 'small'} />
                        </MerchantInfoIconWrapper>
                        <SocialMediaName show={!profilePreview}>{item.name}</SocialMediaName>
                      </AnchorWrapper>
                    </MerchantInfoItem>
                  );
                })}
              </MerchantSocialMedia>
            </MerchantInfo>
          </MerchantInfo>
        </MerchantLeftSide>
        {/* Social Profile Preview Iframe */}
        {profilePreview}
      </MerchantWrapper>
    </SectionContainer>
  );
};
